import { scmpPlus, theme, useResponsive } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { DynamicAdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot/dynamics";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { AppFooter } from "scmp-app/components/app-footer";
import { AuthorContentList } from "scmp-app/components/author/author-content-list";
import { AuthorHeader } from "scmp-app/components/author/author-header";
import { AuthorLinkingData } from "scmp-app/components/author/author-linking-data";
import { AuthorRelatedTopics } from "scmp-app/components/author/author-related-topics";
import { AuthorSeo } from "scmp-app/components/author/author-seo";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { trackingAtom } from "scmp-app/components/tracking";
import { data } from "scmp-app/data";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { parseQueryString } from "scmp-app/lib/utils";
import type { authorAuthorContentListAuthor$key } from "scmp-app/queries/__generated__/authorAuthorContentListAuthor.graphql";
import type { authorPageQuery } from "scmp-app/queries/__generated__/authorPageQuery.graphql";

import {
  Bio,
  Container,
  Header,
  HeaderAdSlotContainer,
  Left,
  LeftAdSlotContainer,
  List,
  Right,
} from "./styles";

const query = graphql`
  query authorPageQuery(
    $entityId: String!
    $applicationIds: [String]
    $after: String
    $count: Int = 30
    $scmpPlusPaywallTypeIds: [String]
  ) {
    author(filter: { entityId: $entityId }) {
      entityId
      entityUuid
      name
      urlAlias
      types
      ...authorContentListAuthor
        @arguments(
          applicationIds: $applicationIds
          after: $after
          count: $count
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...authorAuthorContentListAuthor
        @arguments(
          applicationIds: $applicationIds
          after: $after
          count: $count
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...authorRelatedTopicsAuthor
        @arguments(
          applicationIds: $applicationIds
          after: $after
          count: $count
          scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        )
      ...authorLinkingDataAuthor
      ...authorHeaderAuthor
      ...authorSeoAuthor
    }
  }
`;

const AuthorPage: RelayComponent<unknown, authorPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), true);

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const { entityId, entityUuid, name, types, urlAlias } = data.author;
    trackPageView?.({
      authorName: name,
      authorType: types?.join(", "),
      authors: name,
      contentType: "Author",
      entityId,
      entityUuid,
      page: "Author",
      pageTitle: name,
      path: urlAlias,
    });
  }, [trackPageView, data.author]);

  const { data: contentList, hasNext: hasNextPage } = usePaginationFragment<
    authorPageQuery,
    authorAuthorContentListAuthor$key
  >(
    graphql`
      fragment authorAuthorContentListAuthor on Author
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "authorAuthorContentListPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          exclude: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "authorAuthorContentList__author_contents") {
          edges {
            __typename
          }
        }
      }
    `,
    data.author,
  );

  const totalNumberOfContents = contentList?.contents?.edges?.length ?? 0;

  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  return (
    <>
      <AuthorSeo reference={data.author} />
      <AuthorLinkingData reference={data.author} />
      <Container>
        <Header>
          <HeaderAdSlotContainer>
            {isDesktop && (
              <DynamicAdSlot
                adUnit="d_banner2b"
                sizes={[[970, 250], [728, 90], [970, 90], "fluid"]}
                targeting={{ paid: TargetingPaidType.Free, sctid: data.author.entityId }}
                zone="comment_author/home"
              />
            )}
          </HeaderAdSlotContainer>
        </Header>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          <LeftAdSlotContainer>
            {isDesktop && totalNumberOfContents > 4 && (
              <DynamicAdSlot
                adUnit="d_sky1b"
                sizes={[[120, 160], [160, 600], "fluid"]}
                targeting={{ paid: TargetingPaidType.Free, sctid: data.author.entityId }}
                zone="comment_author/home"
              />
            )}
          </LeftAdSlotContainer>
        </Left>
        <Bio>
          <AuthorHeader reference={data.author} />
        </Bio>
        <List>
          <AuthorContentList reference={data.author} />
        </List>
        <Right>
          <AuthorRelatedTopics reference={data.author} />
        </Right>
      </Container>
      {!hasNextPage && <AppFooter />}
    </>
  );
};

AuthorPage.displayName = "AuthorPage";

export default withRelay(AuthorPage, query, {
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  variablesFromContext(context) {
    return {
      applicationIds: [data.application.scmp.entityUuid],
      entityId: parseQueryString(context.query.entityId) ?? "",
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
    };
  },
});
